import React from 'react';
import { Box, SxProps, Theme } from '@mui/material';

import { ParaLogo } from '@paragon/client-tools';
import { isChMachineUser, isDemoOrganizationUser, isG2User, isTrussWorksUser } from '@paragon/login-tools';

import CHMachineLogo from '../../resources/chlogo.png';
import Demo from '../../resources/demo.png';
import G2Badge from '../../resources/G2Badge.png';
import TrussWorksLogo from '../../resources/trussWorksLogo.png';
import { useAppWithThemeSelector } from './hooks';

interface Props {
  readonly width?: number;
  readonly height?: number;

  readonly clientLogoUrl?: string;
  readonly clientName?: string;

  readonly onClick?: () => void;
  readonly sx?: SxProps<Theme>;
}

export function ThemedLogo({ width, height, clientLogoUrl, clientName, onClick, sx }: Props) {
  const useG2Theme = useAppWithThemeSelector(isG2User);
  const useTrussWorksTheme = useAppWithThemeSelector(isTrussWorksUser);
  const useDemoTheme = useAppWithThemeSelector(isDemoOrganizationUser);
  const useCHTheme = useAppWithThemeSelector(isChMachineUser);

  // We only set a default size if no size values are provided.  This allows `<ThemedLogo width="200" />` or similar
  //   usages to result in an auto-calculated height rather than clobbering it with a default value.
  const hasSizeValues = width || height;
  const finalWidth = hasSizeValues ? width : undefined;
  const finalHeight = hasSizeValues ? height : 50;

  if (useG2Theme) {
    return (
      <Box
        component="img"
        sx={[
          {
            width: finalWidth,
            height: finalHeight,
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        onClick={onClick}
        src={G2Badge}
        alt="G2 Badge"
      />
    );
  }

  if (useTrussWorksTheme) {
    return (
      <Box
        component="img"
        sx={[
          {
            width: finalWidth,
            height: finalHeight,
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        onClick={onClick}
        src={TrussWorksLogo}
        alt="TrussWorks Logo"
      />
    );
  }

  if (useDemoTheme) {
    return (
      <Box
        component="img"
        sx={[
          {
            width: finalWidth,
            height: finalHeight,
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        onClick={onClick}
        src={Demo}
        alt="Demo Logo"
      />
    );
  }

  if (useCHTheme) {
    return (
      <Box
        component="img"
        sx={[
          {
            width: '300px',
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        onClick={onClick}
        src={CHMachineLogo}
        alt="CH Machine Logo"
      />
    );
  }

  if (clientLogoUrl != null && clientName != null) {
    return (
      <Box
        component="img"
        sx={[
          {
            width: 48,
            height: 48,
            // this margin is applied her instead of on the text because the PARAGON text needs to appear closer to the Paragon Logo, but these client logos require a little more margin to make the spacing feel natural. This applies the spacing only to the client logos and not to the text or the Paragon Logo.
            mr: '8px',
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        onClick={onClick}
        src={clientLogoUrl}
        alt={clientName}
      />
    );
  }

  return (
    <ParaLogo sx={sx} onClick={onClick} width={finalWidth} height={finalHeight} viewBox="0 0 32 32" fill="#f4d166" />
  );
}
